import isString from 'lodash/isString'
import isObject from 'lodash/isObject'
import isNil from 'lodash/isNil'
import isFunction from 'lodash/isFunction'
import isEmpty from 'lodash/isEmpty'
import get from 'lodash/get'
import React, { useRef } from 'react'
import { ShowIf } from '@ztechapps/webui.util'
import { useInlineEditor, useMutation } from '@ztechapps/common.hook'
import { InlineEdit } from '../InlineEdit'
import { TextInput } from '../Text/TextInput'

/**
 * This is the inline editor logic, which can reside on web-ui only
 * @param name              - the field name that needs to be updated
 * @param initialValues     - is an object that mus contain at least the _id and the field name that needs to be updated
 *                            it can even contain the whole document, there is no harm, but the _id and field is must and lean and mean
 * @param validate          - a function that will be run before updated
 * @param displayContent    - a component for display value of the field
 * @param editContent       - a component for displaying the edit view of the field, setting it's value to FALSE, will make the control not editable
 * @param emptyContent      - display the empty content, the default is --, but this can be customized per component
 * @param mutationConfig    - a mutation configuration that will be pass to useMutation hook
 * @param updateParams      - we can customized the arguments that we can used of the useMutation (onUpdate) result, here we have the flexibility
 *                            about the input, filter or _id of the onUpdate
 * @param afterUpdate       - this function will be executed after the onUpdate operation
 * @param saveOnEnterKey    - passed this value to inline editor to accept entry when ENTER key was pressed,
 * @param hoverToEdit       - default is true, passing false will need to use the second parameter (onClick) of displayContent to trigger the opening to edit view
 */
export const InlineDisplayEditor = ({
  name,
  initialValues,
  validate,
  displayContent,
  editContent,
  emptyContent,
  mutationConfig,
  updateParams,
  afterUpdate,
  saveOnEnterKey = true,
  hoverToEdit = true,
}: any) => {
  const [onUpdate] = useMutation(mutationConfig)
  const initialValue = get(initialValues, name, '')

  const update = async (updatedValue) => {
    //if new updatedValue and oldValue are the sme dont update it
    //intentional type unsafe comparison
    // eslint-disable-next-line
    if (updatedValue == initialValue) {
      return
    }

    //run the validation (when  available) and let it throw error if there is any
    //make it as async call
    if (validate) {
      await validate(updatedValue)
    }

    //build the default params
    if (updateParams) {
      updateParams = updateParams({ _id: initialValues._id, updatedValue })
    } else {
      updateParams = {
        input: {
          _id: initialValues._id,
          [name]: updatedValue,
        },
      }
    }

    //execute the update now and check for gql errors
    const { errors } = await onUpdate(updateParams)

    //if there is a callback after the operation, then just return it
    if (afterUpdate) {
      await afterUpdate({ input: updateParams.input, errors })
    }
  }

  const inputRef = useRef(null)
  const { editable, saving, onClick, onKeyDown, onBlur, newValue, setNewValue, error, saveNow, cancelNow } = useInlineEditor(
    initialValue,
    update,
    inputRef,
    { saveOnEnterKey }
  )

  //show the default displayContent
  if (displayContent) {
    displayContent = displayContent(initialValue, onClick)
  } else {
    displayContent = initialValue
  }

  //set the default empty content if it not provided
  if (emptyContent) {
    //execute it if a function, else just render the object
    emptyContent = isFunction(emptyContent) ? emptyContent() : emptyContent
  } else {
    emptyContent = <span className="text-warning">--</span>
  }

  const inputProps = {
    meta: { touched: error },
    value: newValue,
    onChange: (e) => setNewValue(e.target.value),
    onKeyDown,
    onBlur,
    submitting: saving,
    inputRef: (e) => (inputRef.current = e),
    noLabel: true,
    saveNow,
    cancelNow,
  }

  if (editContent) {
    editContent = editContent({ ...inputProps, setNewValue })
  } else if (editContent === false) {
    editContent = null
  } else {
    editContent = <TextInput {...inputProps} />
  }

  let isEmptyValue = true

  //if hte initial value is Null or Undefined, then it is empty value
  isEmptyValue = isNil(initialValue) || (isString(initialValue) && isEmpty(initialValue)) || (isObject(initialValue) && isEmpty(initialValue))

  //set the display content
  const displayOnlyContent = (
    <React.Fragment>
      <ShowIf condition={!isEmptyValue}>{displayContent}</ShowIf>
      <ShowIf condition={isEmptyValue}>{emptyContent}</ShowIf>
    </React.Fragment>
  )

  //if the editContent is null, it means there is no editing can be done it just display only
  //so for simplicity, just return it
  if (editContent === null) {
    return displayOnlyContent
  }

  //else it will go the normal edit content display
  return (
    <React.Fragment>
      <ShowIf condition={!editable}>
        <ShowIf condition={hoverToEdit}>
          <InlineEdit.Display className="m-b-none" onClick={onClick}>
            {displayOnlyContent}
          </InlineEdit.Display>
        </ShowIf>
        <ShowIf condition={!hoverToEdit}>
          <InlineEdit.DisplayWrapper className="m-b-none">{displayOnlyContent}</InlineEdit.DisplayWrapper>
        </ShowIf>
      </ShowIf>
      <ShowIf condition={editable}>{editContent}</ShowIf>
    </React.Fragment>
  )
}
